<template>
  <!-- An activity log: the same list in the main menu, on profiles, in tasks and on every History tab (US 9438) -->
  <div class="af" :class="{'af--compact': compact}" data-qid="activity-feed">
    <div v-if="areaFilter || actionFilter || personFilter" class="af__filters">
      <div v-if="areaFilter"
           class="af-seg"
           role="tablist"
           data-qid="activity-feed-areas">
        <button v-for="value in areas"
                :key="'area-' + value"
                type="button"
                role="tab"
                :class="{'is-on': area === value}"
                :aria-selected="area === value ? 'true' : 'false'"
                @click="setFilter('area', value)"
                :data-qid="'activity-feed-area-' + value"
        >{{ $t('profile_page.activity.area.' + value) }}</button>
      </div>
      <div v-if="actionFilter"
           class="af-seg"
           role="tablist"
           data-qid="activity-feed-actions">
        <button v-for="value in actions"
                :key="'action-' + value"
                type="button"
                role="tab"
                :class="{'is-on': action === value}"
                :aria-selected="action === value ? 'true' : 'false'"
                @click="setFilter('action', value)"
                :data-qid="'activity-feed-action-' + value"
        >{{ $t('activity_log.filter.' + value) }}</button>
      </div>
      <b-dropdown v-if="personFilter"
                  class="af-person"
                  variant="link"
                  no-caret
                  toggle-class="af-pick"
                  @shown="searchPeople"
                  data-qid="activity-feed-person"
      >
        <template #button-content>
          <font-awesome-icon icon="users"/>
          <span>{{ person ? person.name : $t('activity_log.everyone') }}</span>
          <font-awesome-icon icon="chevron-down" class="af-pick__caret"/>
        </template>
        <b-dropdown-form @submit.prevent>
          <b-form-input v-model="peopleQuery"
                        size="sm"
                        :placeholder="$t('activity_log.find_person')"
                        @input="searchPeople"
                        data-qid="activity-feed-person-search"
          ></b-form-input>
        </b-dropdown-form>
        <b-dropdown-item @click="setPerson(null)">{{ $t('activity_log.everyone') }}</b-dropdown-item>
        <b-dropdown-item v-for="user in people" :key="'person-' + user.id" @click="setPerson(user)">{{ user.name }}</b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="af__list">
      <template v-for="group in groups">
        <div :key="'day-' + group.day" class="af-day">{{ group.label }}</div>
        <ActivityEntry v-for="entry in group.entries"
                       :key="'entry-' + entry.id"
                       :entry="entry"
                       :compact="compact"
                       :here="hereItem"
                       :hide-context="!!here"
        />
      </template>

      <template v-if="limit || prepared">
        <div v-if="loading" class="af-state"><font-awesome-icon icon="spinner" class="fa-spin"/></div>
        <div v-else-if="!entries.length" class="af-empty af-empty--small" data-qid="activity-feed-empty">{{ emptyText || $t('activity_log.empty_list') }}</div>
      </template>
      <InfiniteLoading v-else :identifier="identifier" @infinite="load">
        <template slot="spinner"><div class="af-state"><font-awesome-icon icon="spinner" class="fa-spin"/></div></template>
        <template slot="no-results">
          <div class="af-empty" data-qid="activity-feed-empty">
            <span class="af-empty__icon"><font-awesome-icon icon="history"/></span>
            <b>{{ emptyText || $t('activity_log.empty_list') }}</b>
            <span v-if="filtered">{{ $t('activity_log.empty_filtered') }}</span>
          </div>
        </template>
        <template slot="no-more"><div class="af-state af-state--end">{{ $t('activity_log.end') }}</div></template>
        <template slot="error"><div class="af-state">{{ $t('activity_log.error') }}</div></template>
      </InfiniteLoading>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import debounce from 'lodash/debounce';
import InfiniteLoading from 'vue-infinite-loading';
import piincHttp from '@/util/configureAxios.js';
import ActivityEntry from './ActivityEntry.vue';
import { describeActivity, kindOf } from './activityTypes';
import { mergeChanges } from './activityChanges';

const PER_PAGE = 25;
// Saves of the same item by the same person this close together are one entry
const BURST_MINUTES = 10;

export default {
  name: 'ActivityFeed',
  components: {
    ActivityEntry,
    InfiniteLoading
  },
  props: {
    // Log name, e.g. App\Process\Models\Process::12, for an item's history
    log: {
      type: String,
      default: null
    },
    // Only this person's changes (profile)
    user: {
      type: [Number, String],
      default: null
    },
    // Other endpoints: (page, filters) => Promise of { items, total_count }
    source: {
      type: Function,
      default: null
    },
    areaFilter: {
      type: Boolean,
      default: false
    },
    actionFilter: {
      type: Boolean,
      default: false
    },
    personFilter: {
      type: Boolean,
      default: false
    },
    // Panels and cards
    compact: {
      type: Boolean,
      default: false
    },
    // The item whose history this is (with who_am_i and slug): it isn't linked to itself
    here: {
      type: Object,
      default: null
    },
    // Cards: only the latest entries, without paging
    limit: {
      type: Number,
      default: 0
    },
    emptyText: {
      type: String,
      default: ''
    },
    // Changing it loads the list again
    reloadKey: {
      default: null
    },
    // Entries described already (summaries from Home and the maintenance overview); nothing is loaded
    prepared: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      items: [],
      page: 1,
      total: 0,
      loading: false,
      identifier: 0,
      areas: ['all', 'projects', 'registers', 'settings'],
      actions: ['all', 'created', 'changed', 'deleted', 'links'],
      area: 'all',
      action: 'all',
      person: null,
      people: [],
      peopleQuery: ''
    };
  },
  computed: {
    client() {
      return this.$store.state.client || {};
    },
    hereItem() {
      return this.here ? { kind: kindOf(this.here.who_am_i), slug: this.here.slug } : null;
    },
    filtered() {
      return this.area !== 'all' || this.action !== 'all' || !!this.person;
    },
    entries() {
      return this.prepared || this.items.map(activity => describeActivity(this, activity));
    },
    // Days, and in each day the entries with saves in a row merged
    groups() {
      const groups = [];
      let group = null;
      let last = null;
      this.entries.forEach(entry => {
        const day = moment(entry.time).format('YYYY-MM-DD');
        if (!group || group.day !== day) {
          group = { day: day, label: this.dayLabel(entry.time), entries: [] };
          groups.push(group);
          last = null;
        }
        if (last && entry.groupKey && last.groupKey === entry.groupKey && moment(last.oldest).diff(moment(entry.time), 'minutes') <= BURST_MINUTES) {
          last.parts.push(entry);
          last.oldest = entry.time;
          return;
        }
        last = { groupKey: entry.groupKey, oldest: entry.time, parts: [entry] };
        group.entries.push(last);
      });
      groups.forEach(day => {
        day.entries = day.entries.map(burst => burst.parts.length === 1 ? burst.parts[0] : Object.assign({}, burst.parts[0], {
          changes: mergeChanges(burst.parts.map(part => part.changes)),
          saves: burst.parts.length
        }));
      });
      return this.limit ? this.limitGroups(groups) : groups;
    }
  },
  watch: {
    reloadKey() {
      this.reset();
    },
    log() {
      this.reset();
    },
    user() {
      this.reset();
    },
    'client.slug'() {
      this.reset();
    }
  },
  methods: {
    dayLabel(value) {
      const days = moment().startOf('day').diff(moment(value).startOf('day'), 'days');
      if (days === 0) {
        return this.$t('activity_log.today');
      }
      if (days === 1) {
        return this.$t('activity_log.yesterday');
      }
      return this.$options.filters.formatDate(value, moment(value).year() === moment().year() ? 'ddd D MMM' : 'ddd D MMM YYYY', true);
    },
    // The first entries up to the limit, in their days
    limitGroups(groups) {
      let left = this.limit;
      const limited = [];
      groups.forEach(day => {
        if (left > 0) {
          const entries = day.entries.slice(0, left);
          left -= entries.length;
          limited.push(Object.assign({}, day, { entries: entries }));
        }
      });
      return limited;
    },
    filterParams() {
      const params = {};
      if (this.area !== 'all') {
        params.area = this.area;
      }
      if (this.action !== 'all') {
        params.action = this.action;
      }
      if (this.person) {
        params.user = this.person.id;
      }
      return params;
    },
    fetch(page) {
      if (this.source) {
        return this.source(page, this.filterParams());
      }
      const params = Object.assign({
        page: page,
        // Cards read a few more, as saves in a row become one entry
        per_page: this.limit ? Math.max(this.limit * 5, 10) : PER_PAGE,
        client: this.client.slug,
        // Only the changed values and the names of related items: logged items carry whole copies of themselves
        compact: 1,
        log: this.log || undefined,
        user: this.user || undefined
      }, this.filterParams());
      return piincHttp.get('activity-log', { params: params }).then(response => response.data);
    },
    load($state) {
      const identifier = this.identifier;
      if (!this.client.slug && !this.source) {
        $state.complete();
        return;
      }
      this.fetch(this.page).then(data => {
        // The list was reloaded meanwhile
        if (identifier !== this.identifier) {
          return;
        }
        this.total = data.total_count || 0;
        if (data.items.length) {
          this.page += 1;
          this.items.push(...data.items);
          $state.loaded();
        }
        if (!data.items.length || this.items.length >= this.total) {
          $state.complete();
        }
      }, () => {
        $state.error();
      });
    },
    // Cards load the latest entries once
    loadLatest() {
      const identifier = this.identifier;
      this.loading = true;
      this.fetch(1).then(data => {
        if (identifier === this.identifier) {
          this.items = data.items;
          this.loading = false;
        }
      }, () => {
        this.loading = false;
      });
    },
    reset() {
      if (this.prepared) {
        return;
      }
      this.items = [];
      this.page = 1;
      this.total = 0;
      this.identifier += 1;
      if (this.limit) {
        this.loadLatest();
      }
    },
    setFilter(name, value) {
      if (this[name] === value) {
        return;
      }
      this[name] = value;
      this.reset();
    },
    setPerson(user) {
      this.person = user;
      this.reset();
    },
    searchPeople: debounce(function() {
      piincHttp.get('users', { params: { client: this.client.id, keyword: this.peopleQuery || undefined, per_page: 50 } }).then(response => {
        this.people = response.data.items || [];
      }, () => {});
    }, 250)
  },
  created() {
    if (this.limit && !this.prepared) {
      this.loadLatest();
    }
  }
};
</script>
